import { format } from "date-fns";
import { get } from "es-toolkit/compat";
import { useState } from "react";
import {
  OperationVariables,
  TypedDocumentNode,
  WatchQueryFetchPolicy,
  useLazyQuery,
} from "@apollo/client";
import { useToast } from "@songtradr-one/components-frontend";
import { useAppContext } from "../../context/app-context/AppContext";
import { errorText } from "../../statics/error-text";
import { staticText } from "../../statics/static-text";

// TEXT
export const text = staticText.exportButton;

// STYLES

// DATA

// TYPES
export interface CsvExportOptions<
  TData,
  TItem extends Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
> {
  query: TypedDocumentNode<TData, TVariables>;
  select: (data: TData) => TItem[] | null | undefined;
  fileName?: string;
  subObjectFields?: string[];
  excludeFields?: string[];
  variables?: TVariables;
  columnLabels?: Record<string, string>;
  fetchPolicy?: WatchQueryFetchPolicy;
  onSuccess?: (count: number) => void;
}

export interface ExportButtonProps<
  TData,
  TItem extends Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
> extends CsvExportOptions<TData, TItem, TVariables> {
  disabled?: boolean;
  appearance?: "responsive" | "icon";
}

// CONSTANTS
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

const FORMULA_PREFIX_PATTERN = /^[=+\-@\t\r]/;
// "-12.50" can't be a formula, so it isn't escaped.
const PLAIN_NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;

// FUNCTIONS
/** Saves CSV content as `<fileName>.csv` through a temporary link. */
export const downloadCsv = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.csv`;
  a.click();
  a.remove();

  window.URL.revokeObjectURL(url);
};

export const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const str =
    typeof value === "string" &&
    FORMULA_PREFIX_PATTERN.test(value) &&
    !PLAIN_NUMBER_PATTERN.test(value)
      ? `'${value}`
      : String(value);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/** This function ensures values are formatted and retrieved correctly for the CSV export.
 * It handles date strings, nested subobject fields, and array values.
 * It uses recursion to apply the date formatting rules to any nested subobject fields or array elements.
 *
 * @param value - The value to be processed. If the value is a date string, it will be formatted to "YYYY-MM-DD HH:mm:ss".
 * If the value is an array, it will be joined into a string separated by ";".
 * If the value is a subobject, the desired field will be retrieved using the `subObjectFields` parameter.
 * @param key - The key or field of the value getting processed. This is used to check if the value is a subobject.
 * @param subObjectFields - An array of strings representing the paths to subobject fields.
 *
 * */
export const processValue = (
  value: unknown,
  key?: string,
  subObjectFields?: string[],
): string | unknown => {
  const isDateTimeString = (str: unknown): str is string =>
    typeof str === "string" &&
    ISO_DATE_TIME_PATTERN.test(str) &&
    !isNaN(Date.parse(str));

  // Retrieve the value of a nested subobject field if it exists
  if (
    key &&
    subObjectFields &&
    subObjectFields.some((path) => path.split(".")[0] === key) && // Check if key matches the first element of any path
    value &&
    typeof value === "object"
  ) {
    const subObjectFieldPath = subObjectFields
      .find((path) => path.split(".")[0] === key)
      ?.split(".")
      .slice(1)
      .join("."); // Get the subobject field path by removing the first element of the path

    if (!subObjectFieldPath) return value;

    const nestedValue = get(value, subObjectFieldPath); // Get the value of the subobject field
    return processValue(nestedValue); // Recursively format the nested value if its a date
  }

  if (typeof value === "string" && ISO_DATE_ONLY_PATTERN.test(value)) {
    return value;
  }

  // Format all date strings to "yyyy-MM-dd HH:mm:ss" for the outputted CSV
  if (isDateTimeString(value)) {
    return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
  } else if (value instanceof Date) {
    return format(value, "yyyy-MM-dd HH:mm:ss");
  }

  // Output all array values to be separated by ";" in the outputted CSV
  if (Array.isArray(value)) {
    return value.map((element) => processValue(element)).join(";"); // Recursively format each element in the array if its a date
  }

  return value; // Return other values as-is
};

/** The export itself, split out so any trigger — a button, a bulk-bar row, a
 * menu item — can run it without inheriting ExportButton's rendering. */
export const useCsvExport = <
  TData,
  TItem extends Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
>({
  query,
  select,
  fileName = "exported_data",
  subObjectFields,
  excludeFields = ["__typename"],
  variables,
  columnLabels = {},
  fetchPolicy,
  onSuccess,
}: CsvExportOptions<TData, TItem, TVariables>) => {
  const { addToast } = useToast();
  const { appConfig } = useAppContext();
  const [fetchData] = useLazyQuery(query, { fetchPolicy });
  const [loading, setLoading] = useState(false);

  const runExport = async () => {
    const response = await fetchData({ variables });

    if (!response.data) {
      throw response.error ?? new Error("Export query returned no data");
    }

    const items = select(response.data);

    if (!items?.length) {
      addToast({
        text: errorText["ExportButton.NoData"].message,
        variant: "error",
      });
      return;
    }

    const exportedItems = items.map((item) => {
      const processedItem: Record<string, unknown> = {};
      Object.entries(item).forEach(([key, value]) => {
        if (!excludeFields.includes(key))
          processedItem[key] = processValue(value, key, subObjectFields);
      });
      return processedItem;
    });

    const columns = Object.keys(exportedItems[0]);
    const rows = exportedItems.map((item) =>
      columns.map((key) => escapeCsvValue(item[key])).join(","),
    );

    const header = columns
      .map((key) => escapeCsvValue(columnLabels[key] ?? key))
      .join(",");
    downloadCsv(header + "\n" + rows.join("\n"), fileName);

    if (onSuccess) onSuccess(exportedItems.length);
    else addToast({ text: text.successMessage, variant: "success" });
  };

  const exportCsv = async () => {
    setLoading(true);
    try {
      await runExport();
    } catch (error) {
      if (!appConfig.production) console.error(error);
      addToast({ text: errorText.default.message, variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return { exportCsv, loading };
};

// ASSETS
